import { Model } from 'src/app/interfaces/api/model';
import { defaultTrack, Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { createExtendedMesh, ExtendedMesh } from 'src/app/interfaces/shared/extendMesh';
import { GPS } from 'src/app/interfaces/shared/gps';
// import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
// import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { Config } from '../../../interfaces/shared/config';
import * as Tools from '../../shared/threeDTools';
import { ColliderShape } from './collider';

export class UnitShape {
    name: string;
    color: string;
    alpha: number = 0.5;
    mesh!: ExtendedMesh;
    id: string;
    visible: boolean = true;
    origin: THREE.Vector3 = new THREE.Vector3();
    projectXY: any = {};
    offset: { x: number; y: number; z: number } = {
        x: 0,
        y: 0,
        z: 0
    };
    colors = Config.colors;
    viewer: any;
    collider: ColliderShape;
    colliders: ColliderShape[];
    trackers: Tracker[];
    icons: any;
    trace: boolean = false;
    show: boolean = true;
    showCollider: boolean = true;
    showTrace: boolean = false;
    traceColor: string = 'dark';
    tracePath: any[] = [];
    prevPosition: any;
    model: Model;
    crane: any;
    geometry: string;
    picture: string;
    lastPosition: Track;
    iconName: string;
    colliderOptions: any = {};

    unitMaterials: any = {
        default: new THREE.MeshPhongMaterial({
            color: this.colors.default,
            transparent: true,
            opacity: 0.75
        }),
        danger: new THREE.MeshPhongMaterial({
            color: this.colors.danger,
            transparent: true,
            opacity: 0.75
        }),
        primary: new THREE.MeshPhongMaterial({
            color: this.colors.primary,
            transparent: true,
            opacity: 0.75
        }),
        warning: new THREE.MeshPhongMaterial({
            color: this.colors.warning,
            transparent: true,
            opacity: 0.75
        }),
        success: new THREE.MeshPhongMaterial({
            color: this.colors.default,
            transparent: true,
            opacity: 0.75
        })
    }

    pathMaterial = new THREE.LineBasicMaterial({
        color: "#000000",
        transparent: false,
        linewidth: 5
    });

    workerMaterial = new THREE.MeshPhongMaterial({
        color: this.colors['worker']
    });

    equipmentMaterial = new THREE.MeshPhongMaterial({
        color: this.colors['equipment']
    });

    craneMaterial = new THREE.MeshPhongMaterial({
        color: this.colors['crane'], // red
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide
    });

    colliderMaterial: any = new THREE.MeshPhongMaterial({
        color: this.colors['danger'],
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false
    });

    eventMaterial = new THREE.MeshPhongMaterial({
        color: this.colors['warning']
    });

    font: any;
    constructor(unit: Unit, viewer: any, model: any, icons: any, font: any) {
        this.name = unit.name;
        this.id = unit.id;
        this.picture = unit.picture;
        this.show = unit.visible;
        this.showTrace = unit.trace;
        this.trace = unit.trace;
        this.font = font;

        if (!unit.collider) {
            unit.collider = {
                offset: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                geometry: "Sphere",
                topradius: 1,
                bottomradius: 1,
                height: 1,
                length: 1,
                width: 1,
                radius: 1,
                visible: true
            }
        }

        this.showCollider = unit.collider.visible ?? true;
        this.geometry = unit.collider.geometry;
        this.crane = unit.crane;
        this.icons = icons
        this.viewer = viewer;
        this.trackers = unit.trackers as Tracker[] || [];
        this.collider = unit.collider as any;
        this.colliders = [];
        this.model = model;
        this.lastPosition = unit.lastPosition as Track;
        let scale = this.model.origin.scale;
        this.color = this.getColor(unit.color);


        this.traceColor = this.color;
        let linewidth: number = 5;

        this.pathMaterial = new THREE.LineBasicMaterial({
            color: unit.color,
            transparent: false,
            linewidth
        })

        let rotation: { x: number; y: number; z: number; } = {
            x: (this.model.origin.xAngle || 0) * Math.PI / 180,
            y: (this.model.origin.yAngle || 0) * Math.PI / 180,
            z: (this.model.origin.zAngle || 0) * Math.PI / 180
        };

        let trackerName: string = '';

        if (this.trackers && this.trackers[0] !== undefined) {
            trackerName = this.trackers[0]?.name;

            if (this.trackers[0].lastTrack) {
                this.lastPosition = this.trackers[0].lastTrack;
            }
        }

        this.picture = unit.picture;
        this.picture = this.picture.substring(this.picture.lastIndexOf('/') + 1);
        this.iconName = this.picture.substring(0, this.picture.lastIndexOf(".")) + ".stl";

        var geometry: any;
        var material: any;

        if (!Object.keys(this.icons).length) return;

        if (this.icons[this.iconName] != undefined) {
            geometry = icons[this.iconName].clone();
        }
        else {
            geometry = icons['worker.stl'].clone();
            this.iconName = 'worker.stl';
        }

        if (this.viewer && this.viewer.model.getData().is2d) {
            let matrix = new THREE.Matrix4();

            matrix.makeRotationY(90 * Math.PI / 180);
            geometry.applyMatrix4(matrix);
            matrix.makeRotationZ((90 - this.model.origin.zAngle) * Math.PI / 180);
            geometry.applyMatrix4(matrix);
        }

        let color = this.unitMaterials[this.color];

        if (color) {
            material = color.clone();
        } else {
            if (this.iconName == 'worker.stl') {
                material = this.workerMaterial.clone();
                material.depthTest = false;
            }
            else {
                material = this.equipmentMaterial.clone();
                material.depthTest = false;
            }
        }

        this.mesh = createExtendedMesh(geometry, material);

        if (trackerName && this.collider && this.collider.geometry !== 'none') {
            this.collider.scale = scale;

            if (this.collider.geometry != "Crane") {
                this.colliderOptions = {
                    trackerId: this.trackers[0]?.id,
                    trackerName: this.trackers[0]?.name,
                    animation: "lookAt",
                    position: this.lastPosition,
                    rotation,
                    scale,
                    material,
                    model: this.model,
                    ...unit.collider
                }

                let collider: ColliderShape = new ColliderShape(this.colliderOptions);
                collider.mesh.visible = this.showCollider;
                this.colliders = [collider];

                this.mesh.init = new THREE.Vector3();


                this.viewer.impl.addOverlay('collider', collider.mesh);
            } else {
                const craneUnit = this.buildCraneUnit(unit);

                if (craneUnit) {
                    const { craneMesh, axisCollider, jibCollider } = craneUnit;
                    axisCollider.mesh.visible = this.showCollider;
                    jibCollider.mesh.visible = this.showCollider;

                    if (craneMesh)
                        this.mesh = craneMesh;

                    this.colliders = [axisCollider, jibCollider];
                }
            }
        }

        // try {
        //     const textMesh = this.getText(this.name);
        //     if (textMesh && textMesh.geometry && (textMesh.geometry as any).attributes) {
        //         this.mesh.add(textMesh);
        //     } else {
        //         console.warn(`UnitShape: Skipping invalid text mesh for "${this.name}"`);
        //     }
        // } catch (error) {
        //     console.error(`UnitShape: Error adding text mesh for "${this.name}":`, error);
        // }

        this.viewer.impl.addOverlay(this.color, this.mesh);
        this.mesh.scale.set(scale, scale, scale);

        if (this.model.origin.angle) {
            var radians = this.model.origin.angle * (Math.PI / 180);
            this.mesh.rotateOnAxis(Tools.axisToOrientationVector(this.model.origin.axis) as any, radians);
        }

        this.mesh.visible = false;
        this.mesh.name = trackerName;
        this.mesh.iconName = this.iconName;
        const hasGPS = !!(this.lastPosition.lat && this.lastPosition.lon);
        const hasXY = !!(this.lastPosition.x || this.lastPosition.y);
        
        if (hasGPS || hasXY) {
            this.updateFromTrack({ ...this.lastPosition });
        } else {
            // console.warn(`[UnitShape] "${this.name}" hidden — no valid GPS or XY position`);
        }

        this.viewer.impl.invalidate(true, true, true);
        this.viewer.impl.sceneUpdated(true);
    }

    getColor(color: any) {
        if (color && color[0] == "#")
            return (Config as any).colorNames[color]
        else {
            return color;
        }
    }

    // getText(text: string) {
    //     if (!this.font) {
    //         console.warn('Font not loaded yet');
    //         return null;
    //     }

    //     if (!(this.font instanceof Font)) {
    //         console.error('❌ this.font is not a Font instance:', this.font);
    //         return null;
    //     }

    //     const geom = new TextGeometry(text, {
    //         font: this.font,
    //         size: 0.4,
    //         depth: 0.05,
    //         curveSegments: 3,
    //         bevelEnabled: false
    //     });

    //     geom.computeBoundingBox();
    //     if (geom.boundingBox) {
    //         const xMid = -0.5 * (geom.boundingBox.max.x - geom.boundingBox.min.x);
    //         geom.translate(xMid, 0.5, 0);
    //     }

    //     const mat = new THREE.MeshBasicMaterial({ color: 0x000000 });
    //     const mesh = new THREE.Mesh(geom as unknown as THREE.BufferGeometry, mat);

    //     if (this.viewer && !this.viewer.model.getData().is2d) {
    //         mesh.rotation.x = 0;
    //         mesh.rotation.y = Math.PI / 2;
    //         mesh.rotation.z = Math.PI / 2;
    //     }

    //     mesh.updateMatrix();
    //     return mesh;
    // }

    // getText(text: string) {
    //     let size: number = 0.4;

    //     let geom = new TextGeometry(text, {
    //         font: new Font(FontJson),
    //         size,
    //         depth: 0.05,
    //         curveSegments: 3,
    //     } as any);

    //     geom.computeBoundingBox();

    //     if (geom.boundingBox) {
    //         geom.translate(geom.boundingBox.max.x * -0.5, 0.5, 0);
    //     }

    //     let bufferGeometry: any;
    //     if (geom.type !== "BufferGeometry") {
    //         try {
    //             const geomAny = geom as any;

    //             if (typeof geomAny.computeFaceNormals === 'function') {
    //                 geomAny.computeFaceNormals();
    //             }

    //             if (typeof geomAny.computeVertexNormals === 'function') {
    //                 geomAny.computeVertexNormals();
    //             }

    //             bufferGeometry = new THREE.BufferGeometry();

    //             let hasValidGeometry = false;

    //             if (typeof (THREE.BufferGeometry as any).fromGeometry === 'function') {
    //                 try {
    //                     bufferGeometry = (THREE.BufferGeometry as any).fromGeometry(geomAny);
    //                     hasValidGeometry = true;
    //                 } catch (fromGeomError) {
    //                     console.warn(`UnitShape: fromGeometry failed for "${text}":`, fromGeomError);
    //                 }
    //             }

    //             if (!hasValidGeometry && geomAny.vertices && geomAny.vertices.length > 0) {
    //                 const positions = new Float32Array(geomAny.vertices.length * 3);

    //                 for (let i = 0; i < geomAny.vertices.length; i++) {
    //                     const vertex = geomAny.vertices[i];
    //                     positions[i * 3] = vertex.x;
    //                     positions[i * 3 + 1] = vertex.y;
    //                     positions[i * 3 + 2] = vertex.z;
    //                 }

    //                 bufferGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    //                 hasValidGeometry = true;
    //             }

    //             if (geomAny.faces && geomAny.faces.length > 0 && hasValidGeometry) {
    //                 const indices = new Uint16Array(geomAny.faces.length * 3);
    //                 const normals = new Float32Array(geomAny.faces.length * 3 * 3);

    //                 for (let i = 0; i < geomAny.faces.length; i++) {
    //                     const face = geomAny.faces[i];
    //                     indices[i * 3] = face.a;
    //                     indices[i * 3 + 1] = face.b;
    //                     indices[i * 3 + 2] = face.c;

    //                     if (face.normal) {
    //                         for (let j = 0; j < 3; j++) {
    //                             normals[i * 9 + j * 3] = face.normal.x;
    //                             normals[i * 9 + j * 3 + 1] = face.normal.y;
    //                             normals[i * 9 + j * 3 + 2] = face.normal.z;
    //                         }
    //                     }
    //                 }

    //                 bufferGeometry.setIndex(new THREE.BufferAttribute(indices, 1));
    //                 bufferGeometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    //             }

    //             if (hasValidGeometry && bufferGeometry.attributes.position) {
    //                 try {
    //                     bufferGeometry.computeBoundingSphere();
    //                 } catch (boundingError) {
    //                     console.warn(`UnitShape: Could not compute bounding sphere for text: "${text}"`, boundingError);
    //                     bufferGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1);
    //                 }
    //             } else {
    //                 console.warn(`UnitShape: No valid geometry found for text: "${text}", creating default geometry`);
    //                 const defaultPositions = new Float32Array([0, 0, 0, 1, 0, 0, 0, 1, 0]);
    //                 bufferGeometry.setAttribute('position', new THREE.BufferAttribute(defaultPositions, 3));
    //                 bufferGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0.5, 0.5, 0), 1);
    //             }
    //         } catch (error) {
    //             console.error(`UnitShape: Error converting TextGeometry to BufferGeometry for text: "${text}"`, error);
    //             bufferGeometry = geom;
    //         }
    //     } else {
    //         bufferGeometry = geom;
    //     }

    //     var mat = new THREE.MeshBasicMaterial({ color: "#000000" });
    //     let textMesh = new THREE.Mesh(bufferGeometry as any, mat);

    //     const geomAny = textMesh.geometry as any;

    //     if (!geomAny || !geomAny.attributes) {
    //         console.warn(`UnitShape: Invalid text mesh geometry for text: "${text}", creating fallback`);
    //         const fallbackGeometry = new THREE.BufferGeometry() as any;
    //         const vertices = new Float32Array([0, 0, 0]);
    //         fallbackGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    //         fallbackGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 0.1);
    //         textMesh.geometry = fallbackGeometry;
    //     }

    //     else if (geomAny.attributes && geomAny.attributes.position && !geomAny.attributes.position.array) {
    //         console.warn(`UnitShape: Position attribute missing array for text: "${text}"`);
    //         const fallbackPositions = new Float32Array([0, 0, 0]);
    //         geomAny.setAttribute('position', new THREE.BufferAttribute(fallbackPositions, 3));
    //     }

    //     if (this.viewer && !this.viewer.model.getData().is2d) {
    //         textMesh.rotateY(90 * Math.PI / 180);
    //         textMesh.rotateZ(90 * Math.PI / 180);
    //     }

    //     textMesh.updateMatrix();

    //     return textMesh
    // }

    update(unit: any) {
        try {
            this.viewer.impl.removeOverlay(this.color, this.mesh);
            this.collider = unit.collider;

            if (unit.name && unit.name != this.name) {
                this.name = unit.name;
            }

            if (unit.picture)
                this.picture = unit.picture;

            if (unit.visible)
                this.show = unit.visible;

            if (unit.trace)
                this.showTrace = unit.trace;

            if (unit.collider && unit.collider.visible)
                this.showCollider = unit.collider.visible;

            if (unit.lastPosition)
                this.lastPosition = unit.lastPosition;

            if (unit.color && unit.color != this.color) {
                this.color = this.getColor(unit.color);
                let color = this.unitMaterials[this.color];

                if (color) {
                    this.mesh.material = color.clone();
                }
            }

            this.mesh.children = [];
            this.mesh.visible = this.show;

            this.viewer.impl.addOverlay(this.color, this.mesh);
            this.viewer.impl.invalidate(true, true, true);
            this.viewer.impl.sceneUpdated(true);
        } catch (e) {
            console.error(e)
        }
    }

    private trackToLocal(track: any): { x: number; y: number; z: number; valid: boolean } {
        const originLat = this.model.origin?.position?.lat || this.model.origin?.position?.mapLat || 0;
        const originLon = this.model.origin?.position?.lon || this.model.origin?.position?.mapLon || 0;
        const scale = this.model.origin?.scale || 1;

        let x: number, y: number, valid: boolean;

        if (track.lat && track.lon) {
            const relXY = Tools.getXYFromLatLonAlt2(track.lat, track.lon, 0, originLat, originLon);
            const angle = this.model.origin?.zAngle || 0;

            const cosA = Math.cos(angle);
            const sinA = Math.sin(angle);

            const rx = (relXY[0] ?? 0) * cosA - (relXY[1] ?? 0) * sinA;
            const ry = (relXY[0] ?? 0) * sinA + (relXY[1] ?? 0) * cosA;

            x = rx * scale - (this.model.origin?.projectXY?.[0] ?? 0);
            y = ry * scale - (this.model.origin?.projectXY?.[1] ?? 0);
            valid = true;
        } else if (track.x || track.y) {
            x = track.x;
            y = track.y;
            valid = true;
        } else {
            x = 0;
            y = 0;
            valid = false;
        }

        const z = ((track.z || 0) - (this.model.origin?.position?.z || 0)) * scale;
        return { x, y, z, valid };
    }

    updateFromTrack(_track: any): void {
        try {
            let track = { ..._track };
            const { x, y, z, valid } = this.trackToLocal(track);

            track.x = x;
            track.y = y;
            track.z = z;

            if (typeof this.mesh === "undefined") {
                return;
            }

            if (!valid) {
                this.mesh.visible = false;
                this.colliders.forEach(c => { c.mesh.visible = false; });
                return;
            }

            this.mesh.visible = true;

            if (this.geometry == "Crane" && this.crane.trackerOnTop && (this.crane.type == "tower" || this.crane.type == "luffing")) {
                this.mesh.position.set(track.x, track.y, track.z - (this.crane.height * this.mesh.scale.z))
            } else {
                this.mesh.position.set(track.x, track.y, track.z);
            }

            this.mesh.updateMatrix();

            this.colliders.forEach(c => {
                c.update(track, c.lookAtTracker ? null as any : defaultTrack, false)
            })

            if (!this.mesh.visible) {
                while (this.tracePath.length) {
                    var line_mesh = this.tracePath.pop();
                    this.viewer.impl.removeOverlay('trace-' + this.traceColor, line_mesh);
                }
            }

            if (this.showTrace === true) {
                var pp = this.prevPosition || track;
                const positions = new Float32Array([
                    pp.x, pp.y, pp.z,
                    track.x, track.y, track.z
                ]);

                const geometry = new THREE.BufferGeometry();
                geometry.addAttribute('position', new THREE.BufferAttribute(positions, 3));

                var lines = new THREE.Line(geometry, this.pathMaterial.clone());

                this.viewer.impl.addOverlay('trace-' + this.traceColor, lines);
                this.tracePath.push(lines);
            } else {
                while (this.tracePath.length) {
                    var line_mesh = this.tracePath.pop();
                    this.viewer.impl.removeOverlay('trace-' + this.traceColor, line_mesh);
                }
            }

            // update the previous position to current
            this.prevPosition = { x: track.x, y: track.y, z: track.z };
            this.lastPosition = _track;
        } catch (e) {
            console.error(e)
        }
    }

    updateFromTarget(_track: Track) {
        if (!this.mesh) return;

        let track = { ..._track }
        const { x, y, z, valid } = this.trackToLocal(track);
        if (!valid) return;

        track.x = x;
        track.y = y;
        track.z = z;

        let towerCenter = this.mesh.position;

        if (towerCenter) {
            if (!this.mesh.init)
                this.mesh.init = new THREE.Vector3();

            var dot = (this.mesh.init.x - towerCenter.x) * (track.x - towerCenter.x) + (this.mesh.init.y - towerCenter.y) * (track.y - towerCenter.y); // dot product
            var det = (this.mesh.init.x - towerCenter.x) * (track.y - towerCenter.y) - (this.mesh.init.y - towerCenter.y) * (track.x - towerCenter.x); // determinant

            var angleRadians = Math.atan2(det, dot);
            this.mesh.init.set(track.x, track.y, track.z);
            this.applyAngleOnCraneUnit(angleRadians * 180 / Math.PI, track)
        }

        if (this.crane.jibTracker) {
            let jibCollider = this.colliders.find(c => c.lookAtTracker === this.crane.jibTracker);

            if (jibCollider) {
                jibCollider.update(null as any, track, true);
            }
        }

        else if (this.crane.hookTracker) {
            let hookCollider = this.colliders.find(c => c.lookAtTracker === this.crane.hookTracker);

            if (hookCollider) {
                hookCollider.update(null as any, track, false);
            }
        }

        this.viewer.impl.invalidate(true, true, true);
        this.viewer.impl.sceneUpdated(true);
    }


    buildCraneUnit(unit: Unit): { craneMesh: any; axisCollider: any; jibCollider: any; } | undefined {
        try {
            let mesh: any;
            let crane: any = unit.crane;
            let scale: number = this.model.origin.scale;

            let rotation = {
                x: (this.model.origin.xAngle || 0) * Math.PI / 180,
                y: (this.model.origin.yAngle || 0) * Math.PI / 180,
                z: (this.model.origin.zAngle || 0) * Math.PI / 180
            };

            let margin = 0;
            const localPos = this.trackToLocal({ ...unit.lastPosition as GPS });
            
            if (!localPos.valid) {
                return undefined;
            }

            let { x, y, z } = localPos;

            crane.position = { x, y, z };
            crane.jibHeight = crane.jibHeight || crane.width || 2;

            let axisColliderOptions: any = {
                geometry: "Axis",
                crane,
                trackerId: (this.trackers[0] ? this.trackers[0].id : this.id) + "_axis",
                trackerName: (this.trackers[0] ? this.trackers[0].name : this.name) + "_axis",
                lookAtTracker: null,
                lookAtAxis: null,
                trackerOnTop: crane.trackerOnTop,
                position: { x, y, z },
                rotation,
                scale,
                offsetAngle: crane.offsetAngle,
                offsetAxis: crane.offsetAxis,
                model: this.model,
                margin
            }

            let jibColliderOptions: any = {
                geometry: "Jib",
                crane,
                trackerId: this.trackers[0] ? this.trackers[0].id : unit.id,
                trackerName: this.trackers[0] ? this.trackers[0].name : unit.name,
                scale,
                lookAtTracker: crane.jibTracker || crane.hookTracker,
                posOffset: new THREE.Vector3(0, 0, 0),
                offsetAngle: crane.offsetAngle,
                offsetAxis: crane.offsetAxis,
                fullHeight: crane.fullHeight,
                model: this.model,
                margin
            }

            if (!crane.trackerOnTop)
                jibColliderOptions.posOffset.z = crane.height * scale;

            if (crane.type == "tower" || crane.type == "luffing") {
                axisColliderOptions.offset = { x: 0, y: 0, z: crane.height * .5 };
                axisColliderOptions.width = (crane.width || 2) + (margin * 2);
                axisColliderOptions.length = (crane.width || 2) + (margin * 2);
                axisColliderOptions.height = crane.height + (margin * 2);

                if (crane.type == "tower") {
                    jibColliderOptions.offset = { x: (crane.jibLength - (crane.counterJibLength || 0)) * .5, y: 0, z: crane.jibHeight * .5 };
                    jibColliderOptions.length = crane.jibLength + (crane.counterJibLength || 0) + (margin * 2);
                } else {
                    jibColliderOptions.offset = { x: crane.jibLength * .5, y: 0, z: crane.jibHeight * .5 };
                    jibColliderOptions.length = crane.jibLength + (margin * 2);
                }

                jibColliderOptions.width = (crane.width || 2) + (margin * 2);
                jibColliderOptions.height = crane.jibHeight + (margin * 2);
                jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("x"), rotation.x);

                if (this.model.origin.up == "z") {
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("y"), rotation.y);
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("z"), rotation.z);

                } else {
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("y"), rotation.z);
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("z"), rotation.y);
                }

                jibColliderOptions.position = { x, y, z };
                jibColliderOptions.fromTracker = false;

                if (crane.fullHeight) {
                    jibColliderOptions.offset.z = crane.height * .5;
                    jibColliderOptions.posOffset = { x: 0, y: 0, z: 0 };
                    jibColliderOptions.height = crane.height + crane.jibHeight + (margin * 2);
                }

                let towerBase: any = new THREE.Mesh(this.icons['tower-base.stl'].clone(), this.equipmentMaterial.clone());
                towerBase.name = unit.id;
                towerBase.scale.set(scale, scale, scale);

                if (crane.trackerOnTop)
                    z -= (crane.height * scale);

                towerBase.position.set(x, y, z);
                towerBase.offsetAxis = crane.offsetAxis;
                towerBase.init = new THREE.Vector3(0, 0, 0);
                towerBase.trackerId = this.trackers[0] ? this.trackers[0].id : unit.id;
                towerBase.options = crane;

                let towerPartCount = Math.ceil((crane.height - 16) * .2)

                for (let i = 0; i < towerPartCount; i++) {
                    let towerPart = new THREE.Mesh(this.icons['tower-part.stl'].clone(), this.equipmentMaterial.clone());

                    towerPart.position.z = (16 + (i * 5));
                    towerPart.updateMatrix();
                    towerBase.add(towerPart);
                }

                let jibBase;
                let jibParent: any = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1));
                jibParent.name = 'jib-parent';
                jibParent.rotation.z = Math.PI * -0.5;
                jibParent.position.z = crane.height + 2;
                jibParent.updateMatrix();

                if (crane.type == "tower") {
                    jibColliderOptions.lookAtType = "ROTATE_2D";

                    if (crane.fullHeight)
                        jibColliderOptions.lookAtType = "ROTATE_2D_CONSTRAINT";

                    towerBase.lookAtType = "ROTATE_2D";
                    let jibPartCount = Math.ceil((crane.jibLength - 20) * .2);
                    jibBase = new THREE.Mesh(this.icons['jib-base.stl'].clone(), this.equipmentMaterial.clone());
                    jibBase.name = 'jib-base'

                    for (let i = 0; i < jibPartCount; i++) {
                        let jibPart = new THREE.Mesh(this.icons['jib-part.stl'].clone(), this.equipmentMaterial.clone());

                        jibPart.position.y = (20.5 + (i * 5));
                        jibPart.name = "jib-part-" + i;
                        jibPart.updateMatrix();
                        jibBase.add(jibPart)
                    }
                } else {
                    jibColliderOptions.lookAtType = "ROTATE_3D_CONSTRAINT";
                    if (crane.fullHeight) jibColliderOptions.lookAtType = "ROTATE_2D"
                    towerBase.lookAtType = "ROTATE_3D_CONSTRAINT";

                    let jibPartCount = Math.ceil(crane.jibLength * .2);

                    jibBase = new THREE.Mesh(this.icons['jib-part.stl'].clone(), this.equipmentMaterial.clone());
                    jibBase.name = 'jib-base'

                    for (let i = 1; i < jibPartCount; i++) {
                        let jibPart = new THREE.Mesh(this.icons['jib-part.stl'].clone(), this.equipmentMaterial.clone());
                        jibPart.name = "jib-part-" + i;
                        jibPart.position.y = (i * 5);
                        jibPart.updateMatrix();
                        jibBase.add(jibPart)
                    }
                }

                jibParent.add(jibBase);
                towerBase.add(jibParent);

                this.viewer.impl.addOverlay('crane', towerBase);
                mesh = towerBase;
            } else if (crane.type == "mobile") {
                axisColliderOptions.offset = { x: 0, y: 0, z: 1 };
                axisColliderOptions.width = 8;
                axisColliderOptions.length = 8;
                axisColliderOptions.height = 2;

                jibColliderOptions.offset = { x: ((crane.jibLength) + (margin * 2)) * .5, y: 0, z: ((crane.jibHeight) + (margin * 2)) * .5 };
                jibColliderOptions.position = { x, y, z };
                jibColliderOptions.posOffset = new THREE.Vector3(0, 0, axisColliderOptions.height * scale);
                jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("x"), rotation.x);

                if (this.model.origin.up == "z") {
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("y"), rotation.y);
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("z"), rotation.z);
                } else {
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("y"), rotation.z);
                    jibColliderOptions.posOffset.applyAxisAngle(Tools.axisToOrientationVector("z"), rotation.y);
                }

                jibColliderOptions.width = (crane.width || 2) + (margin * 2);
                jibColliderOptions.length = crane.jibLength + (margin * 2);
                jibColliderOptions.height = crane.jibHeight + (margin * 2);
                jibColliderOptions.fromTracker = crane.jibTracker ? true : crane.fromTracker;
                jibColliderOptions.lookAtType = crane.jibTracker ? "ROTATE_3D" : "ROTATE_3D_CONSTRAINT";

                if (crane.fullHeight) {
                    jibColliderOptions.lookAtType = "ROTATE_2D"
                    jibColliderOptions.posOffset = { x: 0, y: 0, z: 0 };
                    jibColliderOptions.height = crane.height + crane.jibHeight + (margin * 2);
                    jibColliderOptions.length = crane.jibLength + (margin * 2);
                }

                let geometry: THREE.BoxGeometry = new THREE.BoxGeometry(8, 8, 2);
                let matrix = new THREE.Matrix4();
                matrix.makeTranslation(axisColliderOptions.offset.x || 0, axisColliderOptions.offset.y || 0, axisColliderOptions.offset.z || 0);
                geometry.applyMatrix(matrix);

                let towerBase: any = new THREE.Mesh(geometry, this.equipmentMaterial.clone());
                towerBase.name = unit.id;
                towerBase.scale.set(scale, scale, scale);
                towerBase.position.set(x, y, z);
                towerBase.offsetAxis = crane.offsetAxis;
                towerBase.init = new THREE.Vector3(0, 0, 0);
                towerBase.trackerId = this.trackers[0] ? this.trackers[0].id : unit.id;
                towerBase.options = crane;
                towerBase.lookAtType = crane.jibTracker ? "ROTATE_3D" : "ROTATE_3D_CONSTRAINT";
                towerBase.updateMatrix();

                let jibPartCount = Math.ceil(crane.jibLength * .2);

                let jibParent = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1));
                jibParent.name = 'jib-parent';
                jibParent.position.z = axisColliderOptions.height;
                jibParent.updateMatrix();

                let jibBase = new THREE.Mesh(this.icons['jib-part.stl'].clone(), this.equipmentMaterial.clone());
                jibBase.name = 'jib-base';

                for (let i = 1; i < jibPartCount; i++) {
                    let jibPart = new THREE.Mesh(this.icons['jib-part.stl'].clone(), this.equipmentMaterial.clone());
                    jibPart.position.y = (i * 5);
                    jibPart.name = "jib-part-" + i;
                    jibPart.updateMatrix();
                    jibBase.add(jibPart)
                }

                jibParent.add(jibBase);
                towerBase.add(jibParent);
                mesh = towerBase;
                this.viewer.impl.addOverlay('crane', towerBase);
            }

            let axisCollider = new ColliderShape(axisColliderOptions);
            let jibCollider = new ColliderShape(jibColliderOptions);

            this.viewer.impl.addOverlay('collider', axisCollider.mesh);
            this.viewer.impl.addOverlay('collider', jibCollider.mesh);

            return { craneMesh: mesh, axisCollider, jibCollider };
        } catch (e) {
            console.error(e)
            return undefined;
        }
    }

    lookAt(track: any, up: any) {
        let vector: THREE.Vector3, angle: number = 0, origineZ: number, angleToAdd: number;

        //get the normalized vector of the target
        let target = new THREE.Vector3(track.x, track.y, track.z);
        let position = new THREE.Vector3(this.mesh.position.x, this.mesh.position.y, this.mesh.position.z)
        vector = target.sub(position);

        if (up == "z") {
            //calculate angle
            angle = Math.atan2(vector.y, vector.x);
            origineZ = this.model.origin.angle;
            angleToAdd = origineZ * (Math.PI / 180);
            angle = angle - angleToAdd;

        } else if (up == "y") {
            angle = Math.atan2(vector.z * -1, vector.x);

        } else if (up == "x") {
            angle = Math.atan2(vector.z, vector.y);

        }

        return angle;
    }

    applyAngleOnCraneUnit(angle: number, target: any) {
        let axis = (this.mesh as any).offsetAxis;
        const scale = this.model.origin.scale;

        this.mesh.scale.set(scale, scale, scale);

        let jibParent: any = this.mesh.children.find((j: { name: string; }) => j.name === 'jib-parent') as THREE.Mesh;

        if (!jibParent) return;

        let jibBase: any = jibParent.children[0];

        if (!jibBase) return;

        //get the normalized vector of the target
        target = new THREE.Vector3(target.x, target.y, target.z);

        //calculate angle
        jibBase.rotation[axis] = 0;

        if ((this.mesh as any).lookAtType == "ROTATE_3D") {
            let vector = target.sub(this.mesh.position);
            angle = Math.atan2(vector.y, vector.x);
            let origineZ = this.model.origin.angle;
            let angleToAdd = origineZ * (Math.PI / 180);

            angle = angle - angleToAdd;
            jibParent.rotation[axis] = angle - (Math.PI * .5);

            let up = new THREE.Vector3(0, 0, 1);
            let _angle = (Math.PI * .5) - up.angleTo(vector);

            jibBase.rotation.x = _angle;
        } else if ((this.mesh as any).lookAtType == "ROTATE_2D" || (this.mesh as any).lookAtType == "ROTATE_2D_CONSTRAINT") {
            let angleZRadians = this.lookAt(target, axis);
            jibBase.rotation[axis] = angleZRadians;
        } else if ((this.mesh as any).lookAtType == "ROTATE_3D_CONSTRAINT") {
            let vector = target.sub(this.mesh.position);
            angle = Math.atan2(vector.y, vector.x);
            let origineZ = this.model.origin.angle;
            let angleToAdd = origineZ * (Math.PI / 180);
            let NewAngle = angle - angleToAdd;

            jibParent.rotation[axis] = NewAngle - (Math.PI * .5);

            let xDist: number = vector.x / scale;
            let yDist: number = vector.y / scale;
            let dist: number = Math.abs(Math.sqrt(xDist ** 2 + yDist ** 2));

            if (dist > this.crane.jibLength) dist = this.crane.jibLength;

            let height: number = Math.abs(Math.sqrt(this.crane.jibLength ** 2 - dist ** 2));
            let up: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
            let _angle: number = (Math.PI * .5) - up.angleTo(new THREE.Vector3(xDist, yDist, height));
            jibBase.rotation.x = _angle;
        }

        if (this.colliders) {
            this.colliders.forEach(collider => {
                if (collider.geometryType === "Axis") {
                    collider.mesh.rotation.z = collider.model.origin.angle * (Math.PI / 180)
                }
            });
        }

        jibParent.updateMatrix();
        jibBase.updateMatrix();

        this.viewer.impl.invalidate(true, true, true);
        this.viewer.impl.sceneUpdated(true);
    }

    remove() {
        this.viewer.impl.removeOverlayScene(this.name);
        this.viewer.impl.invalidate(true, true, true);
    };

    toJSON() {
        let unit = {
            id: this.id,
            name: this.name,
            visible: this.show,
            collider: this.collider,
            color: this.colors[this.color],
            trace: this.showTrace,
            alpha: this.alpha
        }

        return unit;
    }
}